import { uniqBy } from "lodash-es";
import { type FC, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Icons,
  Item,
  MenuOptionItem,
  MenuOptionItemIntent,
  type MenuOptionItemProps,
  MenuTriggerButton,
  Section,
  TriggerButtonSize,
} from "widgets";
import { type FileNameInfo, getFilenameInfo } from "widgets/utils";

import { type ProjectFile } from "@/graphql.ts";
import { StackTile } from "@/modules/core/components/StackTiles";
import type { TileProps } from "@/modules/core/components/Tile";
import { FileTile } from "@/modules/files/components/FileTile";
import { getFilesApplicableToGenomeViewer } from "@/modules/files/utils/utils.ts";
import { downloadProjectFiles, getProjectFilesDataForSendingToGenomeViewer } from "@/redux/modules/files.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { EVENT_RESOURCE_TYPES } from "@/settings/settings.ts";
import { FileIcon } from "@/ui-modules/core/FileIcon.tsx";
import BamFileValidationDialog from "@/ui-modules/files/BamFileValidationDialog.tsx";

interface FilesStackTileProps {
  files: ProjectFile[];
  type?: TileProps["type"];
}

export const FilesStackTile: FC<FilesStackTileProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const fileInfos = uniqBy<FileNameInfo | null>(
    props.files.map((file) => getFilenameInfo(file?.fileName)).filter((fileInfo) => fileInfo?.extension),
    (fileInfo) => fileInfo?.extension,
  );
  const [projectFilesForGenomeViewer, setProjectFilesForGenomeViewer] = useState<ProjectFile[] | null>(null);
  const filesApplicableToGenomeViewer = getFilesApplicableToGenomeViewer(props.files);

  const Menu = useMemo(() => {
    const items: { id: string; items: MenuOptionItemProps[] }[] = [
      { id: "more", items: [{ id: "download-all", title: "Download All", icon: "download", intent: undefined }] },
    ];

    if (filesApplicableToGenomeViewer.length) {
      items.unshift({
        id: "send-to",
        items: [
          {
            id: "open-all-genome-viewer",
            title: "Send All to Genome Viewer",
            icon: <img src={Icons.Action.GenomeViewer} width={20} height={20} />,
            intent: MenuOptionItemIntent.Primary,
          },
        ],
      });
    }

    return (
      <MenuTriggerButton
        icon={"more_vert"}
        size={TriggerButtonSize.Small}
        label={""}
        onAction={(key) => {
          switch (String(key)) {
            case "open-all-genome-viewer":
              setProjectFilesForGenomeViewer(
                dispatch(
                  getProjectFilesDataForSendingToGenomeViewer(filesApplicableToGenomeViewer.map((file) => file.id)),
                ),
              );
              break;
            case "download-all":
              dispatch(downloadProjectFiles(props.files.filter((file) => !file.hidden && !file.deleted)));
              break;
          }
        }}
        items={items}
      >
        {(section) => (
          <Section key={section.id} items={section.items}>
            {(item) => (
              <Item key={item.id}>
                <MenuOptionItem {...item} />
              </Item>
            )}
          </Section>
        )}
      </MenuTriggerButton>
    );
  }, [filesApplicableToGenomeViewer, props.files]);

  return (
    <>
      <StackTile
        title={`${props.files.length} ${fileInfos
          .map((fileInfo) => fileInfo?.extension.toUpperCase())
          .join(", ")} files`}
        type={
          props.type === EVENT_RESOURCE_TYPES.UNKNOWN
            ? fileInfos.map((fileInfo) => fileInfo?.typeLabel).join(", ")
            : props.type
        }
        icon={
          <FileIcon
            asStack
            filename={props.files.some((file) => !file.hidden) ? props.files?.[0].fileName : undefined}
          />
        }
        menuButton={
          // do not allow download all when there is at least one restricted file
          props.files.some((file) => !file.hidden && !file.deleted) ? Menu : undefined
        }
      >
        {props.files.map((file) => (
          <FileTile file={file} type={props.type} key={file.id} />
        ))}
      </StackTile>

      {projectFilesForGenomeViewer && (
        <BamFileValidationDialog
          key="file-bam-validation-dialog"
          projectFiles={projectFilesForGenomeViewer}
          onClose={() => {
            setProjectFilesForGenomeViewer(null);
          }}
        />
      )}
    </>
  );
};
